import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Row, Col } from 'antd';
import { EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, BoxPlotOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const Dashboard = () => {
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({ total: 0, online: 0, offline: 0 });
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(15);
  const navigate = useNavigate();

  // --- CONFIG: ADJUST CARD HEIGHT HERE ---
  const CARD_MIN_HEIGHT = '40px'; 
  const CARD_PADDING = '10px 20px';
  // ---------------------------------------

  const fetchData = async () => {
    try {
      const response = await axios.get('/api/box/list/');
      const filteredBoxes = response.data.filter(box => box.id !== 294);
      
      const total = filteredBoxes.length;
      const online = filteredBoxes.filter(box => box.status === 'online').length;
      const offline = filteredBoxes.filter(box => box.status === 'offline').length;
      
      setData(filteredBoxes);
      setStats({ total, online, offline });
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const columns = [
    {
      title: 'Box Name',
      key: 'name_display',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => {
        const nameA = a.box_name || a.box_id || "";
        const nameB = b.box_name || b.box_id || "";
        return nameA.localeCompare(nameB);
      },
      render: (record) => (
        <Space>
          <div style={{ 
            width: '8px', 
            height: '8px', 
            background: record.status === 'online' ? '#34d399' : '#94a3b8', 
            borderRadius: '50%' 
           }}></div>
          <span 
            onClick={() => navigate(`/screendetails/${record.id}`)}
            style={{ 
              color: 'white', 
              fontWeight: '500', 
              cursor: 'pointer', 
              textDecoration: 'none' 
            }}
            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
          >
            {record.box_name || record.box_id}
          </span>
        </Space>
      ),
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      sorter: (a, b) => (a.location || "").localeCompare(b.location || ""),
      render: (text) => <span style={{ color: '#94a3b8' }}>{text || '-'}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (status) => (
        <span style={{ 
          color: status === 'online' ? '#34d399' : '#ef4444', 
          fontWeight: 'bold',
          textTransform: 'uppercase', 
          fontSize: '11px'
        }}>
          {status}
        </span>
      ),
    },
    {
      title: 'Last Seen',
      dataIndex: 'last_seen',
      key: 'last_seen',
      render: (text) => {
        if (!text || text === '-') return '-';
        const date = new Date(text);
        return date.toLocaleString('sr-RS', { 
          day: '2-digit', 
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit', 
          minute: '2-digit',
          second: '2-digit'
        });
      },
    },
    {
      title: 'Uptime',
      dataIndex: 'uptime',
      key: 'uptime',
      render: (text) => <span style={{ color: '#94a3b8' }}>{text || '-'}</span>,
    },
    {
      title: 'CPU',
      dataIndex: 'cpu_load',
      key: 'cpu_load',
      render: (text) => <span style={{ color: '#60a5fa', fontWeight: '500' }}>{text}</span>,
    },
    {
      title: 'RAM',
      dataIndex: 'ram_usage',
      key: 'ram_usage',
      render: (text) => <span style={{ color: '#34d399', fontWeight: '500' }}>{text || '-'}</span>,
    },
    {
      title: 'CPU Temp',
      dataIndex: 'cpu_temp',
      key: 'cpu_temp',
      render: (text) => <span style={{ color: '#fbbf24', fontWeight: '500' }}>{text}</span>,
    },
    {
      title: 'Resolution',
      dataIndex: 'resolution',
      key: 'resolution',
      render: (text) => <span style={{ color: '#64748b' }}>{text || '-'}</span>,
    },
    {
      title: 'Playlist',
      dataIndex: 'current_playlist',
      key: 'playlist',
      render: (text) => <span style={{ color: '#facc15', fontWeight: '500' }}>{text || '-'}</span>,
    },
    {
      title: 'Action',
      key: 'action',
      render: (record) => (
        <Button 
          size="small" 
          icon={<EyeOutlined />} 
          style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }}
          onClick={() => navigate(`/screendetails/${record.id}`)}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '0px' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }} align="stretch">
        <Col span={8}>
          <div style={{ 
            background: '#1e293b', 
            padding: CARD_PADDING, 
            borderRadius: '8px', 
            border: '1px solid #334155', 
            borderBottom: '3px solid #3b82f6',
            minHeight: CARD_MIN_HEIGHT,
            height: '100%',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Space size="middle">
              <BoxPlotOutlined style={{ color: '#3b82f6', fontSize: '24px' }} />
              <div>
                <div style={{ color: '#64748b', fontSize: '12px' }}>TOTAL BOXES</div>
                <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>{stats.total}</div>
              </div>
            </Space>
          </div>
        </Col>
        <Col span={8}>
          <div style={{ 
            background: '#1e293b', 
            padding: CARD_PADDING, 
            borderRadius: '8px', 
            border: '1px solid #334155', 
            borderBottom: '3px solid #34d399',
            minHeight: CARD_MIN_HEIGHT,
            height: '100%',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Space size="middle">
              <CheckCircleOutlined style={{ color: '#34d399', fontSize: '24px' }} />
              <div>
                <div style={{ color: '#64748b', fontSize: '12px' }}>ONLINE</div>
                <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>{stats.online}</div>
              </div>
            </Space>
          </div>
        </Col>
        <Col span={8}>
          <div style={{ 
            background: '#1e293b', 
            padding: CARD_PADDING, 
            borderRadius: '8px', 
            border: '1px solid #334155', 
            borderBottom: '3px solid #ef4444',
            minHeight: CARD_MIN_HEIGHT,
            height: '100%',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Space size="middle">
              <CloseCircleOutlined style={{ color: '#ef4444', fontSize: '24px' }} />
              <div>
                <div style={{ color: '#64748b', fontSize: '12px' }}>OFFLINE</div>
                <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>{stats.offline}</div>
              </div>
            </Space>
          </div>
        </Col>
      </Row>

      <div style={{ 
        background: 'rgba(30, 41, 59, 0.3)', 
        padding: '24px', 
        borderRadius: '12px', 
        border: '1px solid #1e293b' 
      }}>
        <Table 
          dataSource={data} 
          columns={columns} 
          pagination={{ 
            pageSize: pageSize, 
            pageSizeOptions: ['15', '30', '50', '100'], 
            showSizeChanger: true,
            onShowSizeChange: (current, size) => setPageSize(size)
          }}
          rowKey="id"
          loading={loading}
          showSorterTooltip={false}
          style={{ background: 'transparent' }}
        />
      </div>

      <style>{`
        .ant-table { background: transparent !important; color: #94a3b8 !important; }
        .ant-table-thead > tr > th { 
          background: transparent !important; 
          color: #64748b !important; 
          border-bottom: 1px solid #1e293b !important; 
          text-transform: uppercase;
          font-size: 11px;
        }
        .ant-table-thead th.ant-table-column-has-sorters:hover { background: rgba(255,255,255,0.05) !important; }
        .ant-table-thead th.ant-table-column-sort { background: transparent !important; }
        .ant-table-tbody > tr > td { border-bottom: 1px solid #1e293b !important; }
        .ant-table-tbody > tr > td.ant-table-column-sort { background: transparent !important; }
        .ant-table-tbody > tr:hover > td { background: rgba(255,255,255,0.02) !important; }
        .ant-table-column-sorter { color: #64748b !important; }
        .ant-table-column-sorter-up.active, .ant-table-column-sorter-down.active { color: #3b82f6 !important; }
        .ant-pagination-item a { color: #94a3b8 !important; }
        .ant-pagination-item-active { background: #3b82f6 !important; border-color: #3b82f6 !important; }
        .ant-pagination-item-active a { color: white !important; }
        .ant-select-selector { background-color: #1e293b !important; color: white !important; border-color: #334155 !important; }
        .ant-select-arrow { color: #94a3b8 !important; }
        .ant-select-dropdown { background-color: #1e293b !important; border: 1px solid #334155 !important; }
        .ant-select-item { color: #94a3b8 !important; }
        .ant-select-item-option-selected { background-color: #3b82f6 !important; color: white !important; }
      `}</style>
    </div>
  );
};

export default Dashboard;